/**
 * Permission resource model
 * This model does not have defaults as it is intended to be extended for functionality only
 * @class
 */
import Base from '@netx/core/lib/netx-model';

import PermissionsCollection from '@netx/core-permissions/collections/permissions';
import {resourceLabels} from '../properties/permissions';

export default class PermissionResource extends Base {
	/**
	 * Not all models that extend us use the same attribute for their title
	 * Things like attributes (which do not extend Netx.Models.SharedResource) use `name`
	 * @type {string}
	 */
	get titleAttribute() {
		return 'title';
	}

	/**
	 * @override
	 * @param {object} attrs Model attributes
	 * @param {object} opts Model options
	 */
	constructor(attrs, opts = {}) {
		super(attrs, opts);

		/**
		 * Resource type name
		 * Must be set by extending model or at initialize
		 * @type {string}
		 */
		this.resourceName = opts.resourceName;
		// Need a `resourceName` for any permission fetching to work
		if (this.resourceName == null) {
			throw new Error('Permission resource instatiated without `resourceName`');
		}

		/**
		 * @memberof module:share/model/sharedResource
		 * @alias participants
		 * @type {module:permissions/collections/permissions}
		 */
		this.permissions = new PermissionsCollection([], {
			resourceName: this.resourceName,
			resourceId: this.id,
		});
		this.addSubCollection(this.permissions);

		// Hey, listen!
		this.listenTo(this, 'change:id', function (model, id) {
			this.permissions.resourceId = this.id;
		}).listenTo(this.permissions, 'sync', function (permissions) {
			if (permissions !== this.permissions) return;
			// Defer so that all other sync related logic has run
			_.defer(this.permissionsUpdated.bind(this));
		});
	}

	/**
	 * Get applicable permissions
	 * @method
	 * @return {module:permissions/collections/permissions}
	 */
	getPermissions() {
		if (
			!this.permissionsEnabled() ||
			this.isNew()
			// Just resolve so the normal workflow can occur
		)
			return new $.Deferred().resolve();

		return this.permissions.fetch();
	}

	/**
	 * @method
	 * @return {string}
	 */
	getResourceLabel() {
		return resourceLabels[this.resourceName] || '';
	}

	/**
	 * @method
	 * @return {string}
	 */
	getTitle() {
		return this.get(this.titleAttribute) || '';
	}

	/**
	 * @method
	 * @return {boolean}
	 */
	hasParticipants() {
		return this.permissions.participants.length !== 0;
	}

	/**
	 * Are permissions for this resource enabled
	 * Override this method as needed - default return is `true`
	 * @override
	 * @return {boolean}
	 */
	permissionsEnabled() {
		return true;
	}

	/**
	 * @method
	 */
	permissionsUpdated() {
		this.set('hasPermissions', this.hasParticipants());
	}

	/** @type {string} */
	static get __name__() {
		return 'PermissionResource';
	}
}
